import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from "react";
import axios from "axios";
import { engine } from "@/audio/engine";
if (typeof window !== "undefined") window.__engine = engine;

const StudioContext = createContext(null);
export const useStudio = () => useContext(StudioContext);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export function StudioProvider({ children }) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [bpm, setBpmState] = useState(120);
  const [currentStep, setCurrentStep] = useState(-1);
  const [currentBar, setCurrentBar] = useState(0);
  const [currentPatStep, setCurrentPatStep] = useState(-1);
  const [currentInstance, setCurrentInstance] = useState(0);
  const [metronome, setMetronome] = useState(false);
  const [selectedChannel, setSelectedChannel] = useState("drums");
  const [activeTab, setActiveTab] = useState("decks");
  const [manualFocus, setManualFocus] = useState(null);
  const [chopSource, setChopSource] = useState(null);
  const [timelineVersion, setTimelineVersion] = useState(0);
  const [tick, setTick] = useState(0); // force re-render for engine state changes
  const [projectName, setProjectName] = useState("Untitled Session");
  const [currentProjectId, setCurrentProjectId] = useState(null);
  const [crate, setCrate] = useState([]);
  const [deckInfo, setDeckInfo] = useState({
    deckA: { name: "— empty —", loaded: false },
    deckB: { name: "— empty —", loaded: false },
  });
  const stepCbSet = useRef(false);

  const isAudioFile = (f) =>
    f && (f.type?.startsWith("audio") || /\.(mp3|wav|ogg|m4a|aac|flac)$/i.test(f.name));

  // Load a File/Blob, a crate item ({file}) or a cloud track ({url}) onto a deck.
  const loadToDeck = useCallback(async (deckId, source, name) => {
    let ab, displayName;
    if (source instanceof Blob) {
      if (!isAudioFile(source)) throw new Error("not audio");
      ab = await source.arrayBuffer();
      displayName = source.name || name || "track";
    } else if (source && source.file) {
      ab = await source.file.arrayBuffer();
      displayName = source.name || source.file.name;
    } else if (source && source.url) {
      const res = await axios.get(`${BACKEND_URL}${source.url}`, { responseType: "arraybuffer" });
      ab = res.data;
      displayName = source.name || name || "track";
    } else {
      throw new Error("no source");
    }
    const shown = (displayName || "track").slice(0, 28);
    await engine.loadDeck(deckId, ab, shown);
    setDeckInfo((d) => ({ ...d, [deckId]: { name: shown, loaded: true } }));
  }, []);

  // Drop a beat/audio onto a Timeline track so it loops in sync with the transport —
  // the user can then hit Play and build drums/instruments over it on Beats & Keys.
  const loadToTimeline = useCallback(async (source) => {
    engine.ensureStarted();
    let ab, displayName;
    if (source instanceof Blob) {
      ab = await source.arrayBuffer();
      displayName = source.name || "beat";
    } else if (source && source.file) {
      ab = await source.file.arrayBuffer();
      displayName = source.name || source.file.name;
    } else if (source && source.url) {
      const res = await axios.get(`${BACKEND_URL}${source.url}`, { responseType: "arraybuffer" });
      ab = res.data;
      displayName = source.name || "beat";
    } else {
      throw new Error("no source");
    }
    const buf = await engine.ctx.decodeAudioData(ab.slice(0));
    let target = Object.keys(engine.trackBuffers).find((id) => !engine.trackBuffers[id]);
    if (!target) target = engine.addTrack();
    engine.setTrackBuffer(target, buf);
    setActiveTab("timeline");
    setTimelineVersion((v) => v + 1);
    return { trackId: target, name: (displayName || "beat").slice(0, 28) };
  }, []);

  const uploadTrack = async (file) => {
    const form = new FormData();
    form.append("file", file);
    const res = await axios.post(`${API}/uploads`, form, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return res.data; // { id, name, url }
  };

  // Add files to the crate; if signed in, upload them so they persist across sessions.
  const addToCrate = useCallback(async (files) => {
    const arr = Array.from(files).filter(isAudioFile);
    const items = arr.map((f) => ({
      id: `local-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      name: f.name,
      file: f,
      local: true,
      uploading: !!sessionStorage.getItem("nebula_token"),
    }));
    setCrate((c) => [...c, ...items]);
    if (sessionStorage.getItem("nebula_token")) {
      for (const it of items) {
        try {
          const up = await uploadTrack(it.file);
          setCrate((c) =>
            c.map((x) => (x.id === it.id ? { ...x, id: up.id, url: up.url, local: false, uploading: false } : x))
          );
        } catch (e) {
          setCrate((c) => c.map((x) => (x.id === it.id ? { ...x, uploading: false } : x)));
        }
      }
    }
    return arr.length;
  }, []);

  const loadCloudCrate = useCallback(async () => {
    if (!sessionStorage.getItem("nebula_token")) return;
    try {
      const res = await axios.get(`${API}/uploads`);
      setCrate((prev) => {
        const cloud = res.data.map((d) => ({ id: d.id, name: d.name, url: d.url, local: false }));
        const ids = new Set(cloud.map((c) => c.id));
        const localOnly = prev.filter((x) => x.local && !ids.has(x.id));
        return [...cloud, ...localOnly];
      });
    } catch (e) {
      /* ignore */
    }
  }, []);

  const removeFromCrate = useCallback(async (id) => {
    setCrate((c) => c.filter((x) => x.id !== id));
    if (!String(id).startsWith("local-")) {
      try {
        await axios.delete(`${API}/uploads/${id}`);
      } catch (e) {}
    }
  }, []);
  const clearCrate = useCallback(() => setCrate([]), []);

  // ---- Vocal remover / stem separation ----
  const separateStems = useCallback(async (file) => {
    const form = new FormData();
    form.append("file", file);
    const res = await axios.post(`${API}/stems/separate`, form, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 240000,
    });
    return res.data; // { stems: {vocals,drums,bass,other}, remaining, name }
  }, []);

  const loadInstrumentalToDeck = useCallback(async (deckId, stems, name) => {
    const parts = ["drums", "bass", "other"]
      .filter((p) => stems[p])
      .map((p) => `${BACKEND_URL}${stems[p].url}`);
    const buffers = await Promise.all(parts.map((u) => engine.decodeUrl(u)));
    const mix = engine.mixBuffers(buffers);
    const shown = `${(name || "track").slice(0, 18)} · no vox`;
    engine.setDeckBuffer(deckId, mix, shown);
    setDeckInfo((d) => ({ ...d, [deckId]: { name: shown, loaded: true } }));
  }, []);

  const addStemToCrate = useCallback((stem) => {
    setCrate((c) => [...c, { id: stem.id, name: stem.name, url: stem.url, local: false }]);
  }, []);

  // ---- AI Beat generation (fal.ai: ACE-Step Fast / Stable Audio Master) ----
  const generateBeat = useCallback(async ({ prompt, engine: eng = "fast", duration = 30, mode = "loop" }) => {
    const res = await axios.post(
      `${API}/generate/beat`,
      { prompt, engine: eng, duration, mode },
      { timeout: 240000 }
    );
    const item = { id: res.data.id, name: res.data.name, url: res.data.url, local: false };
    setCrate((c) => [item, ...c]);
    return res.data; // { id, name, url, engine, duration }
  }, []);

  // Seamlessly extend a generated Master beat past its window (no splicing).
  const extendBeat = useCallback(async ({ mediaId, addSeconds = 30 }) => {
    const res = await axios.post(
      `${API}/generate/extend`,
      { media_id: mediaId, add_seconds: addSeconds },
      { timeout: 240000 }
    );
    const item = { id: res.data.id, name: res.data.name, url: res.data.url, local: false };
    setCrate((c) => [item, ...c]);
    return res.data;
  }, []);

  // Push any track (e.g. a generated beat or crate item) into the Sample Chopper to slice & play.
  const sendToChopper = useCallback((item) => {
    setChopSource({ url: item.url, name: item.name || "Beat" });
    setActiveTab("sampler");
  }, []);

  const openManual = useCallback((section) => {
    setManualFocus(section || null);
    setActiveTab("manual");
  }, []);

  const forceUpdate = useCallback(() => setTick((t) => t + 1), []);

  // Build the audio graph up-front (suspended) so the mixer & rack render on load,
  // then restore the last working session (EQ, mixer, patterns, bpm…) on the studio route.
  useEffect(() => {
    try {
      engine.ensureStarted();
      if (engine.ctx && engine.ctx.state === "running") engine.ctx.suspend();
      const onStudio = typeof window !== "undefined" && window.location.pathname.startsWith("/studio");
      if (onStudio) {
        const saved = localStorage.getItem("beatmxr_session");
        if (saved) {
          const parsed = JSON.parse(saved);
          engine.load(parsed);
          setBpmState(engine.bpm);
          if (parsed.__name) setProjectName(parsed.__name);
          if (parsed.__projectId) setCurrentProjectId(parsed.__projectId);
        }
      }
      forceUpdate();
    } catch (e) {
      /* AudioContext will init on first gesture instead */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-save the working session to localStorage so settings survive logout/refresh.
  useEffect(() => {
    const unlock = () => {
      try {
        engine.unlock();
      } catch (e) {}
    };
    window.addEventListener("touchend", unlock);
    window.addEventListener("pointerdown", unlock);
    window.addEventListener("mousedown", unlock);
    window.addEventListener("keydown", unlock);
    return () => {
      window.removeEventListener("touchend", unlock);
      window.removeEventListener("pointerdown", unlock);
      window.removeEventListener("mousedown", unlock);
      window.removeEventListener("keydown", unlock);
    };
  }, []);

  // Auto-save the working session to localStorage so settings survive logout/refresh.
  useEffect(() => {
    if (!(typeof window !== "undefined" && window.location.pathname.startsWith("/studio"))) return;
    const save = () => {
      try {
        const data = engine.serialize();
        data.__name = projectName;
        data.__projectId = currentProjectId;
        localStorage.setItem("beatmxr_session", JSON.stringify(data));
      } catch (e) {
        /* ignore quota/serialize errors */
      }
    };
    const iv = setInterval(save, 3000);
    const onHide = () => document.hidden && save();
    window.addEventListener("beforeunload", save);
    document.addEventListener("visibilitychange", onHide);
    return () => {
      save();
      clearInterval(iv);
      window.removeEventListener("beforeunload", save);
      document.removeEventListener("visibilitychange", onHide);
    };
  }, [projectName, currentProjectId]);

  // When switching tabs, point the Effects Rack at the most relevant channel
  // so EQ/FX edits affect what the user is actually hearing on that tab.
  useEffect(() => {
    const map = { decks: "deckA", beats: "drums", player: "player" };
    const target = map[activeTab];
    if (target && engine.channels && engine.channels[target]) {
      setSelectedChannel(target);
    }
  }, [activeTab]);

  const ensureStepCallback = useCallback(() => {
    if (!stepCbSet.current) {
      engine.onStep = (s, bar, patStep, instance) => {
        setCurrentStep(s);
        if (typeof bar === "number") setCurrentBar(bar);
        if (typeof patStep === "number") setCurrentPatStep(patStep);
        if (typeof instance === "number") setCurrentInstance(instance);
      };
      stepCbSet.current = true;
    }
  }, []);

  const undo = useCallback(() => {
    if (engine.undo()) {
      setBpmState(engine.bpm);
      forceUpdate();
    }
  }, [forceUpdate]);

  const redo = useCallback(() => {
    if (engine.redo()) {
      setBpmState(engine.bpm);
      forceUpdate();
    }
  }, [forceUpdate]);

  const pushHistory = useCallback(() => engine.pushHistory(), []);

  const play = useCallback(() => {
    engine.play();
    ensureStepCallback();
    setIsPlaying(true);
  }, [ensureStepCallback]);

  const stop = useCallback(() => {
    engine.stop();
    setIsPlaying(false);
    setCurrentStep(-1);
    setCurrentPatStep(-1);
  }, []);

  const togglePlay = useCallback(() => {
    if (engine.isPlaying) stop();
    else play();
  }, [play, stop]);

  const setBpm = useCallback((v) => {
    engine.setBpm(v);
    setBpmState(v);
  }, []);

  const toggleMetronome = useCallback(() => {
    engine.ensureStarted();
    setMetronome(engine.toggleMetronome());
  }, []);

  const saveProject = useCallback(async () => {
    engine.ensureStarted();
    const payload = { name: projectName, data: engine.serialize() };
    if (currentProjectId) {
      const res = await axios.put(`${API}/projects/${currentProjectId}`, payload);
      return res.data;
    }
    const res = await axios.post(`${API}/projects`, payload);
    setCurrentProjectId(res.data.id);
    return res.data;
  }, [projectName, currentProjectId]);

  const listProjects = useCallback(async () => {
    const res = await axios.get(`${API}/projects`);
    return res.data;
  }, []);

  const deleteProject = useCallback(async (id) => {
    await axios.delete(`${API}/projects/${id}`);
    if (id === currentProjectId) setCurrentProjectId(null);
  }, [currentProjectId]);

  const loadProject = useCallback((proj) => {
    engine.load(proj.data);
    setProjectName(proj.name);
    setCurrentProjectId(proj.id);
    setBpmState(engine.bpm);
    forceUpdate();
  }, [forceUpdate]);

  const newProject = useCallback(() => {
    setCurrentProjectId(null);
    setProjectName("Untitled Session");
  }, []);

  const generateSongStarter = useCallback(async (prompt) => {
    engine.ensureStarted();
    if (engine.ctx && engine.ctx.state === "suspended") engine.ctx.resume();
    const res = await axios.post(`${API}/ai/song-starter`, { prompt }, { timeout: 45000 });
    await engine.applySongStarter(res.data);
    setBpmState(engine.bpm);
    forceUpdate();
    return res.data;
  }, [forceUpdate]);

  const shareProject = useCallback(async () => {
    engine.ensureStarted();
    let id = currentProjectId;
    if (!id) {
      const payload = { name: projectName, data: engine.serialize() };
      const res = await axios.post(`${API}/projects`, payload);
      id = res.data.id;
      setCurrentProjectId(id);
    } else {
      await axios.put(`${API}/projects/${id}`, { name: projectName, data: engine.serialize() });
    }
    const res = await axios.post(`${API}/projects/${id}/share`);
    return res.data; // { share_id, cover_url }
  }, [currentProjectId, projectName]);

  // Create a share link for a specific saved project (used by My Songs list).
  const shareProjectById = useCallback(async (id) => {
    const res = await axios.post(`${API}/projects/${id}/share`);
    return res.data; // { share_id, cover_url }
  }, []);

  // AI songwriting co-writer — aware of the current beat's tempo & key.
  const assistLyrics = useCallback(async ({ mode, prompt = "", existing = "" }) => {
    const res = await axios.post(
      `${API}/ai/lyrics`,
      { mode, prompt, existing, bpm: engine.bpm || 120, key: engine.songKey || "" },
      { timeout: 45000 }
    );
    return res.data.text;
  }, []);

  // Generate AI cover art for a saved project (for social share cards).
  const generateCover = useCallback(async (id) => {
    const pid = id || currentProjectId;
    if (!pid) return null;
    const res = await axios.post(`${API}/projects/${pid}/cover`, {}, { timeout: 60000 });
    return res.data.cover_url;
  }, [currentProjectId]);

  const value = {
    engine,
    isPlaying,
    bpm,
    setBpm,
    currentStep,
    currentBar,
    currentPatStep,
    currentInstance,
    metronome,
    toggleMetronome,
    play,
    stop,
    togglePlay,
    selectedChannel,
    setSelectedChannel,
    activeTab,
    setActiveTab,
    manualFocus,
    setManualFocus,
    openManual,
    undo,
    redo,
    pushHistory,
    tick,
    forceUpdate,
    projectName,
    setProjectName,
    saveProject,
    listProjects,
    deleteProject,
    loadProject,
    newProject,
    generateSongStarter,
    shareProject,
    shareProjectById,
    generateCover,
    assistLyrics,
    currentProjectId,
    crate,
    addToCrate,
    removeFromCrate,
    clearCrate,
    loadCloudCrate,
    separateStems,
    loadInstrumentalToDeck,
    addStemToCrate,
    generateBeat,
    extendBeat,
    sendToChopper,
    chopSource,
    setChopSource,
    deckInfo,
    loadToDeck,
    loadToTimeline,
    timelineVersion,
  };

  return <StudioContext.Provider value={value}>{children}</StudioContext.Provider>;
}
